import React, { useMemo } from 'react';

import { useReactNativeProps } from '@eika-infrastruktur/mobilbank-lib';

import { useConfig } from '@/default-config';
import { useCreditCardDetails } from '@/domain/card';
import { useCard } from '@/react-query/hooks/query/use-card';

import { CreditCardWalletType } from '@generated-types';

import useApplePayEligibleCardQuery from '../../hooks/query/use-apple-pay-eligible-card-query';
import { usePendingCards } from '../../hooks/query/use-pending-cards';
import { useEligibleCreditCardsQueryForApple } from '../../queries';
import { AppleWalletService } from '../../services';
import { ApplePayContent } from './ApplePayContent';

export const ApplePayKbQuery = () => {
    const cardQuery = useCreditCardDetails();

    const eligibleCardsQuery = useEligibleCreditCardsQueryForApple({
        select: (data) => data.filter((card) => card.walletType === CreditCardWalletType.APPLE_PAY),
    });

    if (cardQuery.isPending || cardQuery.isError || eligibleCardsQuery.isError || eligibleCardsQuery.isPending) {
        return null;
    }

    const eligibleCard = eligibleCardsQuery.data.find((card) => card.id === cardQuery.data.id);

    if (eligibleCard) {
        return (
            <ApplePayContent
                data={{
                    source: 'kb',
                    card: cardQuery.data,
                    eligibleCard,
                }}
            />
        );
    }

    return null;
};

export const ApplePayTeQuery = () => {
    const nativeProps = useReactNativeProps();
    const { minAppVersionDebitCardEnrollment } = useConfig();

    const cardQuery = useCard(true);

    const pendingTokensQuery = usePendingCards(true);

    const operationAllowed = useMemo(
        () => Boolean(cardQuery.data?.allowedOperations.setWallets),
        [cardQuery.data?.allowedOperations.setWallets],
    );
    const eligibleCardsQuery = useApplePayEligibleCardQuery(nativeProps.appleWallet, operationAllowed, true);

    if (cardQuery.isPending || cardQuery.isError || pendingTokensQuery.isError || eligibleCardsQuery.isError) {
        return null;
    }

    if (pendingTokensQuery.isLoading || eligibleCardsQuery.isPending) {
        return null;
    }

    const targetPendingTokens = !pendingTokensQuery.isPending
        ? pendingTokensQuery.data.filter((pendingToken) => pendingToken.eikaCardId === cardQuery.data.id)
        : [];

    const targetEligibleCard = eligibleCardsQuery.data.find(
        (availableCard) => availableCard.eikaCardId === cardQuery.data.id,
    );

    if (!targetEligibleCard) {
        return null;
    }

    if (!AppleWalletService.canEnrollTeCard(nativeProps, cardQuery.data, minAppVersionDebitCardEnrollment)) {
        return null;
    }

    return (
        <ApplePayContent
            data={{
                source: 'te',
                card: cardQuery.data,
                eligible: targetEligibleCard,
                pendingTokens: targetPendingTokens,
            }}
        />
    );
};
